import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
} from "@mui/material";

export interface QuickCreateModalProps<T = unknown> {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Form content (minimal fields) */
  children: React.ReactNode;
  /** Parent submits (e.g. create mutation); returns created entity. Modal calls onSuccess(entity) then onClose(). */
  onSubmit: () => Promise<T>;
  /** Called with created entity when submit succeeds */
  onSuccess: (entity: T) => void;
  submitDisabled?: boolean;
  loading?: boolean;
  submitLabel?: string;
}

/**
 * Modal for quick-create flows (e.g. "Create new Location" from within Transfer form).
 * Parent provides onSubmit (create mutation); on success, modal calls onSuccess(created) then onClose().
 */
export default function QuickCreateModal<T = unknown>({
  open,
  onClose,
  title,
  children,
  onSuccess,
  onSubmit,
  submitDisabled = false,
  loading = false,
  submitLabel = "Save",
}: QuickCreateModalProps<T>) {
  const handleSubmit = async () => {
    try {
      const created = await onSubmit();
      onSuccess(created);
      onClose();
    } catch {
      // Caller shows toast / field errors
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>{title}</DialogTitle>
      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>{children}</Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitDisabled || loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
        >
          {loading ? "Saving…" : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
